import { getCourierAppUrl } from './lib/courierApp'
import { lazy } from 'react'
import { redirect } from 'react-router-dom'
import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom'
import { getAccessToken, getRefreshToken, refreshAccessToken } from './lib/auth'
import { fetchMe } from './lib/api'
import { RouteErrorPage } from './components/PageLoading'

const MarketingPage = lazy(() => import('./pages/site/MarketingPage'))
const ProductPage = lazy(() => import('./pages/site/ProductPage'))
const PlansPage = lazy(() => import('./pages/site/PlansPage'))
const ProposalPage = lazy(() => import('./pages/site/ProposalPage'))
const LoginPage = lazy(() => import('./pages/site/LoginPage'))
const HelpPage = lazy(() => import('./pages/site/HelpPage'))
const LegalPage = lazy(() => import('./pages/site/LegalPage'))
const NotFoundPage = lazy(() => import('./pages/site/NotFoundPage'))
const PreferencesPage = lazy(() => import('./pages/account/PreferencesPage'))
const ChangePasswordPage = lazy(() => import('./pages/account/ChangePasswordPage'))
const NotificationsPage = lazy(() => import('./pages/account/NotificationsPage'))
const AboutPage = lazy(() => import('./pages/account/AboutPage'))
const AdminLayout = lazy(() => import('./layouts/AdminLayout'))
const adminPages = {
  dashboard: lazy(() => import('./pages/admin/DashboardPage')),
  deliveries: lazy(() => import('./pages/admin/DeliveriesPage')),
  newDelivery: lazy(() => import('./pages/admin/NewDeliveryPage')),
  deliveryDetail: lazy(() => import('./pages/admin/DeliveryDetailPage')),
  invoices: lazy(() => import('./pages/admin/InvoicesPage')),
  companies: lazy(() => import('./pages/admin/CompanySettingsPage')),
  team: lazy(() => import('./pages/admin/TeamPage')),
  reports: lazy(() => import('./pages/admin/ReportsPage')),
  usage: lazy(() => import('./pages/admin/PlanUsagePage')),
  billing: lazy(() => import('./pages/admin/BillingPage')),
  legal: lazy(() => import('./pages/admin/AccountLegalPage')),
  help: lazy(() => import('./pages/admin/HelpPage')),
  newHelp: lazy(() => import('./pages/admin/HelpPage').then(module => ({ default: module.NewHelpTicketPage }))),
  helpDetail: lazy(() => import('./pages/admin/HelpPage').then(module => ({ default: module.HelpTicketDetailPage }))),
  settings: lazy(() => import('./pages/admin/AccountConfigurationPage')),
}

async function requireManager({ request }: LoaderFunctionArgs) {
  let token = getAccessToken()
  if (!token && getRefreshToken()) token = await refreshAccessToken()
  // React Router uses thrown Responses for data-router redirects.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (!token) {
    const url = new URL(request.url)
    const destination = `${url.pathname}${url.search}${url.hash}`
    throw redirect(`/login?next=${encodeURIComponent(destination)}`)
  }
  const me = await fetchMe()
  if (!me.memberships.some(m => m.is_active && m.role === 'manager')) {
    // React Router uses thrown Responses for route errors.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw new Response(null, { status: 403, statusText: 'Seu perfil não tem acesso a esta área.' })
  }
  return null
}

export const routes: RouteObject[] = [
  { path: '/', element: <MarketingPage />, errorElement: <RouteErrorPage /> },
  { path: '/ajuda', element: <HelpPage />, errorElement: <RouteErrorPage /> },
  { path: '/contato', element: <MarketingPage />, errorElement: <RouteErrorPage /> },
  { path: '/produto', element: <ProductPage />, errorElement: <RouteErrorPage /> },
  { path: '/planos', element: <PlansPage />, errorElement: <RouteErrorPage /> },
  { path: '/custom', element: <PlansPage />, errorElement: <RouteErrorPage /> },
  { path: '/proposta', element: <ProposalPage />, errorElement: <RouteErrorPage /> },
  { path: '/privacidade', element: <LegalPage />, errorElement: <RouteErrorPage /> },
  { path: '/termos', element: <LegalPage />, errorElement: <RouteErrorPage /> },
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/criar-conta', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/recuperar-acesso', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/nova-senha', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/aceitar-convite', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/escolher-conta', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  // Resolve the legacy entry point using the active role and app origin.
  { path: '/app', loader: async ({ request }) => {
    if (!getAccessToken() && !getRefreshToken()) return redirect('/login')
    if (!getAccessToken() && !await refreshAccessToken()) return redirect('/login')
    const me = await fetchMe()
    if (me.memberships.some(membership => membership.is_active && membership.role === 'manager')) {
      const next = new URL(request.url).searchParams.get('next')
      return redirect(next?.startsWith('/admin') ? next : '/admin')
    }
    if (me.memberships.some(membership => membership.is_active && membership.role === 'courier')) {
      return redirect(getCourierAppUrl())
    }
    throw new Response(null, { status: 403, statusText: 'Seu perfil não tem acesso a esta área.' })
  } },
  { path: '/admin', loader: requireManager, element: <AdminLayout />, errorElement: <RouteErrorPage />, children: [
    { index: true, element: <adminPages.dashboard /> },
    { path: 'entregas', element: <adminPages.deliveries /> },
    { path: 'entregas/nova', element: <adminPages.newDelivery /> },
    { path: 'entregas/:id', element: <adminPages.deliveryDetail /> },
    { path: 'notas-fiscais', element: <adminPages.invoices /> },
    { path: 'cnpjs', element: <adminPages.companies /> },
    { path: 'equipe', element: <adminPages.team /> },
    { path: 'relatorios', element: <adminPages.reports /> },
    { path: 'plano-e-consumo', element: <adminPages.usage /> },
    { path: 'cobrancas', element: <adminPages.billing /> },
    { path: 'assinatura-e-dados', element: <adminPages.legal /> },
    { path: 'ajuda', element: <adminPages.help /> },
    { path: 'ajuda/novo', element: <adminPages.newHelp /> },
    { path: 'ajuda/:id', element: <adminPages.helpDetail /> },
    { path: 'configuracoes', element: <adminPages.settings /> },
    { path: 'preferencias', element: <PreferencesPage /> },
    { path: 'alterar-senha', element: <ChangePasswordPage /> },
    { path: 'notificacoes', element: <NotificationsPage /> },
    { path: 'sobre', element: <AboutPage /> },
  ] },
  { path: '*', element: <NotFoundPage /> },
]
